import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import i18n from '../i18n';

// API Configuration - Centralized base URL for all API calls
// In development, uses VITE_API_BASE_URL from .env.development (https://localhost:5001/api)
// In production, set VITE_API_BASE_URL environment variable
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api';

type CustomHeaders = Record<string, string>;

const getAccessToken = () =>
  localStorage.getItem('auth_token') ||
  localStorage.getItem('token') ||
  localStorage.getItem('access_token');

const getRefreshToken = () => localStorage.getItem('auth_refresh_token');

const storeTokens = (accessToken: string, refreshToken?: string | null) => {
  localStorage.setItem('auth_token', accessToken);
  if (refreshToken) {
    localStorage.setItem('auth_refresh_token', refreshToken);
  }
};

// Wird aufgerufen, wenn das Access-Token abgelaufen UND das Refresh-Token
// ebenfalls ungültig/abgelaufen ist. Räumt die gespeicherte Session auf und
// schickt den Nutzer zur Login-Seite, statt die App im "eingeloggt, aber
// leer" Zustand hängen zu lassen.
export const handleUnauthorized = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_refresh_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');

  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// Tauscht das Refresh-Token gegen ein frisches Access-/Refresh-Token-Paar.
// Mehrere gleichzeitige 401s (z.B. die parallelen Dashboard-Requests) teilen
// sich denselben In-Flight-Request, statt jede für sich das (rotierende,
// also nur einmal gültige) Refresh-Token einzulösen und sich gegenseitig
// ungültig zu machen.
let refreshPromise: Promise<string | null> | null = null;

export const refreshAccessToken = (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.resolve(null);
  }

  refreshPromise = fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
    .then(async (response) => {
      if (!response.ok) return null;
      const data = await response.json().catch(() => null);
      const payload = data?.data ?? data;
      const newAccessToken: string | undefined = payload?.accessToken;
      if (!newAccessToken) return null;
      storeTokens(newAccessToken, payload?.refreshToken);
      return newAccessToken;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

// Helper function to make fetch requests with common headers
export const fetchApi = async (endpoint: string, options: RequestInit = {}) => {
  const token = getAccessToken();

  // FormData bodies (file uploads) must NOT get an explicit Content-Type —
  // the browser sets it itself, including the multipart boundary.
  const isFormData = options.body instanceof FormData;

  const baseHeaders: CustomHeaders = isFormData ? {} : {
    'Content-Type': 'application/json',
  };
  baseHeaders['Accept-Language'] = i18n.language;

  // Merge existing headers from options, converting to our type
  const existingHeaders = options.headers as CustomHeaders || {};

  const headers: CustomHeaders = {
    ...baseHeaders,
    ...existingHeaders,
  };

  // Ein bereits in options.headers gesetzter Authorization-Header (z.B. ein 2FA-Pending-Token)
  // hat Vorrang vor dem in localStorage gespeicherten regulären Token, und wird nicht über
  // den Refresh-Flow erneuert - für Pending-Tokens gibt es kein Refresh, bei 401 muss der
  // Nutzer den Login-Vorgang neu starten.
  const usesDefaultAuth = !!token && !headers['Authorization'];
  if (usesDefaultAuth) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers,
  };

  try {
    let response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (response.status === 401 && usesDefaultAuth) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers: { ...headers, Authorization: `Bearer ${newToken}` },
        });
      }
    }

    if (response.status === 401) {
      handleUnauthorized();
      throw new Error(i18n.t('common:errors.sessionExpired'));
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
      throw new Error(errorData.message || errorData.details || `HTTP error! status: ${response.status}`);
    }

    // Handle responses that might not have JSON body (e.g., 204 No Content)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error(`API fetch error (${endpoint}):`, error);
    throw error;
  }
};

// Wie fetchApi, aber für Datei-Downloads (z.B. Export) — liefert Blob + Dateiname statt JSON.
// options erlaubt z.B. einen POST mit FormData (Stammblatt: Titelseite hochladen, PDF zurück).
export const fetchApiBlob = async (endpoint: string, options: RequestInit = {}): Promise<{ blob: Blob; filename: string }> => {
  const requestWith = (token: string | null) =>
    fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Accept-Language': i18n.language,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

  let response = await requestWith(getAccessToken());

  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      response = await requestWith(newToken);
    }
  }

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error(i18n.t('common:errors.sessionExpired'));
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message || errorData.details || `HTTP error! status: ${response.status}`);
  }

  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : 'export';

  return { blob: await response.blob(), filename };
};

// Zentrale axios-Instanz für Stellen, die (anders als fetchApi) mit axios arbeiten
// (z.B. Dashboard.tsx). Request-Interceptor haengt automatisch das Access-Token an,
// Response-Interceptor versucht bei 401 einmalig ein Refresh + Retry, bevor er
// aufgibt und zum Login schickt.
export const apiClient: AxiosInstance = axios.create({ baseURL: API_BASE_URL });

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  config.headers = config.headers ?? ({} as InternalAxiosRequestConfig['headers']);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['Accept-Language'] = i18n.language;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers = originalRequest.headers ?? ({} as InternalAxiosRequestConfig['headers']);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
      handleUnauthorized();
    }

    return Promise.reject(error);
  }
);
